import { useCallback, useMemo, useState } from "react";
import { danger, success } from "@";
import type { useUpdateStatusMutation } from "../api/mutations";
import type { VerificationStage, VerifySearchValues } from "../types.ts";

export interface ReviewState {
  open: boolean;
  verificationStageId: number | null;
  attachmentIndex: number;
}

interface UseReviewFlowParams {
  data: VerificationStage[];
  draftValues: VerifySearchValues;
  acceptOrdersMutation: ReturnType<typeof useUpdateStatusMutation>;
}

export interface UseReviewFlowReturn {
  reviewState: ReviewState;
  reviewOrder: VerificationStage | null;
  openReview: (verificationStageId: number, attachmentIndex: number) => void;
  closeReview: () => void;
  handleReviewAccept: (order: VerificationStage) => Promise<void>;
  handleReviewReject: (
    order: VerificationStage,
    comment: string,
  ) => Promise<void>;
  isReviewSubmitting: boolean;
}

export const useReviewFlow = ({
  data,
  draftValues,
  acceptOrdersMutation,
}: UseReviewFlowParams): UseReviewFlowReturn => {
  const [reviewState, setReviewState] = useState<ReviewState>({
    open: false,
    verificationStageId: null,
    attachmentIndex: 0,
  });

  const openReview = useCallback(
    (verificationStageId: number, attachmentIndex: number) => {
      setReviewState({ open: true, verificationStageId, attachmentIndex });
    },
    [],
  );

  const closeReview = useCallback(() => {
    setReviewState({
      open: false,
      verificationStageId: null,
      attachmentIndex: 0,
    });
  }, []);

  // Resolve the clicked order from the current data array.
  const reviewOrder = useMemo(() => {
    if (!reviewState.open || reviewState.verificationStageId == null)
      return null;
    return (
      data.find(
        (o) => o.verificationStageId === reviewState.verificationStageId,
      ) ?? null
    );
  }, [data, reviewState.open, reviewState.verificationStageId]);

  const handleReviewAccept = useCallback(
    async (order: VerificationStage) => {
      if (order.verificationStageId === undefined) return;
      try {
        await acceptOrdersMutation.mutateAsync({
          verificationStagesIds: [order.verificationStageId],
          isAccepted: true,
          comment: "",
          searchValues: draftValues,
          selectingAll: false,
        });
        await success("تم تاكيد الطلب");
        closeReview();
      } catch (e) {
        danger("تعذر تأكيد الطلب");
        throw e;
      }
    },
    [acceptOrdersMutation, draftValues, closeReview],
  );

  const handleReviewReject = useCallback(
    async (order: VerificationStage, comment: string) => {
      if (order.verificationStageId === undefined) return;
      try {
        await acceptOrdersMutation.mutateAsync({
          verificationStagesIds: [order.verificationStageId],
          isAccepted: false,
          comment,
          searchValues: draftValues,
          selectingAll: false,
        });
        await success("تم رفض الطلب");
        closeReview();
      } catch (e) {
        danger("تعذر رفض الطلب");
        throw e;
      }
    },
    [acceptOrdersMutation, draftValues, closeReview],
  );

  return {
    reviewState,
    reviewOrder,
    openReview,
    closeReview,
    handleReviewAccept,
    handleReviewReject,
    isReviewSubmitting: acceptOrdersMutation.isPending,
  };
};
